import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { assetCatalog } from "@/api/client";
import { Field } from "@/components/Field";
import { PageHead } from "@/components/PageHead";
import { useKindParam } from "@/lib/hooks/useKindParam";
import {
  KindSelect, ModelChip, ModelForm, ModelSelect, QK, kindLabel, useModelPool, useTemplates,
} from "@/modules/asset_catalog/common";

/** Product Models — two buttons (Add new · Edit existing) → picker → the
 *  template-driven form. Nothing else lives here; lists and filters are the
 *  Overview's job.
 *
 *  ONE TAB FOR EVERY KIND. Dattatray: *"we dont need 3 subpages under Asset
 *  Catalog ... We can have Batteries, Vehicle and Subcomponents in single
 *  tab ... This way in a single tab we should be able to manage addition and
 *  edit of all product models. Its easier for usability standpoint!"*
 *
 *  Batteries, Vehicles and Subcomponents were always THIS component with a
 *  narrower `kinds` prop, so the three tabs were one page pretending to be
 *  three — and they carried a real bug for it: rendering the same component
 *  type at the same tree position means React reconciles instead of
 *  remounting, so switching tabs kept the previous one's `pickKind` and
 *  `mode` (D-84). Distinct `key`s fixed that. One tab removes the situation
 *  rather than the symptom.
 *
 *  `kinds` stays a parameter because it is still the thing that narrows the
 *  picker — it is simply "all of them" now. */
export function AssetCatalogActionPage({ eyebrow, title, desc, kinds }: {
  eyebrow: string; title: string; desc: string;
  /** which kinds this page may add or edit. Empty = every kind there is,
   *  which is what the single Product Models tab passes. */
  kinds: string[];
}) {
  const [sp, setSp] = useSearchParams();
  const tpl = useTemplates().data;
  const pool = useModelPool().data?.models ?? [];
  // An EMPTY `kinds` means every kind. Resolved from the templates the
  // server sends, so a product added in Products shows up here without this
  // page keeping its own list — which is the same one-source rule 0064
  // applied to the spec itself.
  const allKinds = (tpl?.kinds ?? []).map((k) => k.id);
  const scope = kinds.length ? kinds : allKinds;
  const single = scope.length === 1;

  const [mode, setMode] = useState<"idle" | "new" | "pick" | "edit">("idle");
  // Seeded from `?kind=` so "Models" in a product's drawer lands on THAT
  // product, not on an empty picker (Dattatray, 2026-09-28).
  const [pickKind, setPickKind] = useKindParam();
  const [editId, setEditId] = useState<string | null>(null);

  // deep link from the Overview drawer (?id=)
  useEffect(() => {
    const id = sp.get("id");
    if (id) { setEditId(id); setMode("edit"); }
  }, [sp]);

  const detail = useQuery({
    queryKey: [...QK, "detail", editId], queryFn: () => assetCatalog.model(editId!), enabled: !!editId,
  });
  const m = detail.data;
  const reset = () => { setMode("idle"); setEditId(null); if (sp.get("id")) setSp({}, { replace: true }); };

  // The kind actually in play: the one picked, or the only one there is.
  const activeKind = pickKind || (single ? scope[0] : "");
  const candidates = pool.filter(
    (p) => (activeKind ? p.kind === activeKind : scope.includes(p.kind))
      && p.status !== "retired");
  // Was hardcoded to "Subcomponent" whenever more than one kind was in
  // scope — correct while the only multi-kind tab WAS Subcomponents, and
  // wrong the moment one tab covers everything. It now names the kind being
  // worked on, and says "model" until one is chosen.
  const crumb = activeKind ? kindLabel(tpl, activeKind) : "model";

  return (
    <div className="mpage on">
      <PageHead eyebrow={eyebrow} title={title} desc={desc}
        right={mode !== "idle" ? <button className="btn ghost" onClick={reset}>← Back</button> : undefined} />

      {mode === "idle" && (
        <div className="panel panel-pad" style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
          <button className="btn teal" onClick={() => { setEditId(null); setMode("new"); }}>Add new</button>
          <button className="btn ghost" onClick={() => setMode("pick")}>Edit existing</button>
          <span className="cap">
            Existing models are listed with filters on the Overview page.
          </span>
        </div>
      )}

      {mode === "pick" && (
        <div className="panel panel-pad" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 14 }}>
          {!single && (
            <Field label="Kind" req>
              <KindSelect kinds={tpl?.kinds ?? []} only={scope} value={pickKind} onChange={setPickKind} style={{ width: "100%", minWidth: 0 }} />
            </Field>
          )}
          <Field label="Model" req>
            <ModelSelect rows={candidates} value="" style={{ width: "100%" }}
              placeholder={candidates.length ? "— choose model —" : "— nothing to edit yet —"}
              onChange={(id) => { if (id) { setEditId(id); setMode("edit"); } }} />
          </Field>
        </div>
      )}

      {mode === "new" && (
        <div className="panel panel-pad">
          <div className="section-label">New {crumb.toLowerCase()} model · v1</div>
          <ModelForm kinds={scope} crumb={`New ${crumb.toLowerCase()}`} onCancel={reset}
            onDone={(created) => { setEditId(created.version_id); setMode("edit"); }} />
        </div>
      )}

      {mode === "edit" && (
        <div className="panel panel-pad">
          {!m && <div className="cap">{detail.isError ? "Model not found." : "Loading…"}</div>}
          {m && (
            <>
              <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 700 }}>{m.name}</span>
                <span className="mono muted">v{m.version}</span>
                <ModelChip st={m.status} />
                <span className="cap">{kindLabel(tpl, m.kind)} · {m.manufacturer}</span>
                {m.status === "active" && (
                  <span style={{ marginLeft: "auto", color: "var(--t-mute)", fontSize: 12 }}>
                    Active versions are immutable — your edits become v{m.version + 1} (draft).
                  </span>
                )}
                {m.versions.some((v) => v.status === "draft" && v.version_id !== m.version_id) && (
                  <span style={{ marginLeft: "auto", color: "var(--warn)", fontSize: 12 }}>
                    A draft already exists on this line —{" "}
                    <a href="#" onClick={(e) => { e.preventDefault(); setEditId(m.versions.find((v) => v.status === "draft")!.version_id); }}>edit that one</a>.
                  </span>
                )}
              </div>
              {/* Keyed for the same reason the tabs are: ModelForm seeds its
                  fields, BOM rows and visibility list from `model` in useState
                  initialisers, which run ONLY on mount. Unkeyed, moving to a
                  different model re-renders the old state under the new title. */}
              <ModelForm key={m.version_id} kinds={[m.kind]} model={m} crumb={`${m.name} v${m.version}`}
                onDone={(saved) => setEditId(saved.version_id)} onDeleted={reset} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
